import { CATEGORIES, type Category, SEVERITIES, type Severity } from '@opensentry/shared';
import { CATEGORY_DISPLAY } from '@opensentry/shared';
import { classNames } from '../lib/format.js';
import { CategoryIcon } from './CategoryIcon.js';
import { SEVERITY_COLORS } from './SeverityPill.js';

export interface Filters {
  severities: Set<Severity>;
  categories: Set<Category>;
}

/**
 * Click-to-solo, then additive. If everything in the group is selected,
 * a click solos that one. Otherwise it toggles membership; emptying the
 * group falls back to "all" so the map is never blank.
 */
function cycle<T>(set: Set<T>, v: T, all: readonly T[]): Set<T> {
  if (set.size >= all.length) return new Set([v]);
  const next = new Set(set);
  next.has(v) ? next.delete(v) : next.add(v);
  return next.size === 0 ? new Set(all) : next;
}

export function FilterBar({
  filters,
  onChange,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {SEVERITIES.map((s) => {
        const active = filters.severities.has(s);
        return (
          <button
            key={s}
            type="button"
            onClick={() =>
              onChange({ ...filters, severities: cycle(filters.severities, s, SEVERITIES) })
            }
            className={classNames(
              'rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide transition',
              active ? 'text-white' : 'text-[var(--color-muted)]',
            )}
            style={{
              background: active ? SEVERITY_COLORS[s] : 'var(--color-panel-2)',
              border: `1px solid ${active ? SEVERITY_COLORS[s] : 'var(--color-border)'}`,
            }}
          >
            {s}
          </button>
        );
      })}
      <span className="mx-1 h-4 w-px bg-[var(--color-border)]" />
      {CATEGORIES.map((cat) => {
        const active = filters.categories.has(cat);
        return (
          <button
            key={cat}
            type="button"
            onClick={() =>
              onChange({ ...filters, categories: cycle(filters.categories, cat, CATEGORIES) })
            }
            className={classNames(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition',
              active
                ? 'bg-[var(--color-panel-2)] text-[var(--color-text)]'
                : 'text-[var(--color-muted)]',
            )}
            style={{ border: `1px solid var(--color-border)` }}
          >
            <CategoryIcon category={cat} size={12} />
            {CATEGORY_DISPLAY[cat].label}
          </button>
        );
      })}
    </div>
  );
}

export function emptyFilters(): Filters {
  return { severities: new Set(SEVERITIES), categories: new Set(CATEGORIES) };
}
